import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import './AlertModal.css';

const VARIANT_CONFIGS = {
  error: {
    icon: '❌',
    primaryColor: '#dc2626',
    iconBg: '#fee2e2',
    borderColor: '#fecaca',
  },
  warning: {
    icon: '⚠️',
    primaryColor: '#d97706',
    iconBg: '#fef3c7',
    borderColor: '#fde68a',
  },
  success: {
    icon: '✅',
    primaryColor: '#16a34a',
    iconBg: '#d1fae5',
    borderColor: '#a7f3d0',
  },
  info: {
    icon: 'ℹ️',
    primaryColor: '#3b82f6',
    iconBg: '#dbeafe',
    borderColor: '#bfdbfe',
  },
  custom: {
    icon: null,
    primaryColor: '#6b7280',
    iconBg: '#f3f4f6',
    borderColor: '#e5e7eb',
  },
};

const PRESENTATION_DEFAULTS = {
  classic: { overlayOpacity: 0.5, overlayBlur: 4 },
  prompt: { overlayOpacity: 0.16, overlayBlur: 2 },
};

/** Build overlay background from rgb triplet "15, 23, 42", hex, or full css color. */
function resolveOverlayBackground(overlayColor, overlayOpacity) {
  if (!overlayColor) {
    return `rgba(15, 23, 42, ${overlayOpacity})`;
  }
  const trimmed = String(overlayColor).trim();
  if (/^rgba?\(/i.test(trimmed)) {
    return trimmed;
  }
  if (trimmed.startsWith('#')) {
    return trimmed;
  }
  if (trimmed.includes(',')) {
    return `rgba(${trimmed}, ${overlayOpacity})`;
  }
  return trimmed;
}

/**
 * Unified alert / confirmation modal.
 *
 * presentation:
 * - `classic` — centered dialog with icon (auth errors, blocking alerts)
 * - `prompt` — compact sheet with slide-up animation (profile confirmations)
 *
 * Overlay: `overlayColor` (rgb triplet or css), `overlayOpacity` (0–1), `overlayBlur` (px)
 */
const AlertModal = ({
  isOpen,
  onClose,
  variant = 'info',
  presentation = 'classic',
  subject = '',
  body = '',
  icon = null,
  primaryColor = null,
  showCloseButton = true,
  allowOverlayClose = true,
  autoClose = false,
  autoCloseDelay = 3000,
  actions = [],
  details = null,
  style = {},
  zIndex = 1000,
  overlayOpacity = null,
  overlayColor = null,
  overlayBlur = null,
}) => {
  const isPrompt = presentation === 'prompt';
  const defaults = PRESENTATION_DEFAULTS[isPrompt ? 'prompt' : 'classic'] || PRESENTATION_DEFAULTS.classic;
  const effectiveOpacity = overlayOpacity ?? defaults.overlayOpacity;
  const effectiveBlur = overlayBlur ?? defaults.overlayBlur;

  useEffect(() => {
    if (isOpen && autoClose && onClose) {
      const timer = setTimeout(() => onClose(), autoCloseDelay);
      return () => clearTimeout(timer);
    }
  }, [isOpen, autoClose, autoCloseDelay, onClose]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const scrollY = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      window.scrollTo(0, scrollY);
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const baseConfig = VARIANT_CONFIGS[variant] || VARIANT_CONFIGS.info;
  const config = {
    ...baseConfig,
    icon: icon !== null ? icon : baseConfig.icon,
    primaryColor:
      variant === 'custom' && primaryColor ? primaryColor : baseConfig.primaryColor,
  };

  const overlayStyle = {
    zIndex,
    backgroundColor: resolveOverlayBackground(overlayColor, effectiveOpacity),
    backdropFilter: effectiveBlur > 0 ? `blur(${effectiveBlur}px)` : 'none',
    WebkitBackdropFilter: effectiveBlur > 0 ? `blur(${effectiveBlur}px)` : 'none',
  };

  const panelStyle = {
    ...style,
    '--alert-accent': config.primaryColor,
    ...(isPrompt
      ? {}
      : {
          '--alert-border': config.borderColor,
        }),
  };

  const handleOverlayClick = (e) => {
    if (allowOverlayClose && e.target === e.currentTarget && onClose) {
      onClose();
    }
  };

  const defaultActions =
    actions.length === 0 ? [{ label: 'Close', onClick: onClose, variant: 'secondary' }] : actions;

  const renderAction = (action, index) => {
    const handleActionClick = (e) => {
      if (action.href) {
        if (action.onClick) action.onClick(e);
        if (action.closeOnClick && onClose) onClose();
        return;
      }
      if (action.onClick) action.onClick(e);
      if (action.closeOnClick && onClose) onClose();
    };

    const variantClass =
      action.variant === 'secondary'
        ? 'alert-modal-btn--secondary'
        : action.variant === 'danger' ||
            (action.variant === 'primary' && action.style?.background === '#dc2626')
          ? 'alert-modal-btn--danger'
          : 'alert-modal-btn--primary';

    if (isPrompt) {
      const btn = (
        <button
          key={index}
          type="button"
          className={`alert-modal-btn ${variantClass}`}
          onClick={action.href ? undefined : handleActionClick}
          style={
            action.variant === 'primary' && action.style?.background
              ? { background: action.style.background, borderColor: action.style.background }
              : undefined
          }
        >
          {action.label}
        </button>
      );
      if (action.href) {
        return (
          <Link key={index} to={action.href} onClick={handleActionClick} style={{ flex: '1 1 auto', minWidth: 0 }}>
            {btn}
          </Link>
        );
      }
      return btn;
    }

    const buttonContent = (
      <button
        key={index}
        type="button"
        className={`alert-modal-btn alert-modal-btn--classic ${variantClass}`}
        onClick={action.href ? undefined : handleActionClick}
        style={
          action.variant === 'primary' && action.style?.background
            ? {
                background: action.style.background,
                borderColor: action.style.background,
              }
            : action.style
        }
      >
        {action.label}
      </button>
    );

    if (action.href) {
      return (
        <Link
          key={index}
          to={action.href}
          onClick={handleActionClick}
          style={{ textDecoration: 'none' }}
        >
          {buttonContent}
        </Link>
      );
    }
    return buttonContent;
  };

  const overlayClass = [
    'alert-modal-overlay',
    isPrompt && 'alert-modal-overlay--prompt',
  ]
    .filter(Boolean)
    .join(' ');

  const panelClass = [
    isPrompt ? 'alert-modal-panel--prompt' : 'alert-modal-panel--classic',
  ].join(' ');

  const modalContent = (
    <div className={overlayClass} style={overlayStyle} onClick={handleOverlayClick}>
      <div
        className={panelClass}
        style={panelStyle}
        role="dialog"
        aria-modal="true"
        aria-labelledby={subject ? 'alert-modal-title' : undefined}
        onClick={(e) => e.stopPropagation()}
      >
        {(subject || showCloseButton) && (
          <div className="alert-modal-header">
            <div className="alert-modal-header-main">
              {!isPrompt && config.icon ? (
                <div className="alert-modal-icon" style={{ background: config.iconBg }}>
                  {config.icon}
                </div>
              ) : null}
              {subject ? (
                <h2
                  id="alert-modal-title"
                  className="alert-modal-title"
                  style={!isPrompt ? { color: config.primaryColor } : undefined}
                >
                  {subject}
                </h2>
              ) : null}
            </div>
            {showCloseButton && (
              <button
                type="button"
                className="alert-modal-close"
                onClick={onClose}
                aria-label="Close"
              >
                ×
              </button>
            )}
          </div>
        )}

        {(body || details) && (
          <div className="alert-modal-body">
            {body && <p>{body}</p>}
            {details && (
              <div className="alert-modal-details">
                {typeof details === 'string' ? details : JSON.stringify(details, null, 2)}
              </div>
            )}
          </div>
        )}

        {defaultActions.length > 0 && (
          <div className="alert-modal-actions">{defaultActions.map(renderAction)}</div>
        )}
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default AlertModal;
